// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    ProgressBar,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnId,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellProps,
    TableRow,
    Tooltip,
    createTableColumn,
    makeStyles,
    shorthands,
    tokens,
    useTableFeatures,
    useTableSort,
} from '@fluentui/react-components';
import {
    CloudLink20Regular,
    DocumentArrowUp20Regular,
    DocumentPdfRegular,
    DocumentTextRegular,
    FluentIconsProps,
} from '@fluentui/react-icons';
import * as React from 'react';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useChat } from '../../../libs/hooks';
import { ChatMemorySource, DocumentUploadState } from '../../../libs/models/ChatMemorySource';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { setImportingDocumentsToConversation } from '../../../redux/features/conversations/conversationsSlice';
import { timestampToDateString } from '../../utils/TextUtils';
import { TabView } from './TabView';
import { IFundInfo, IQuestionsStatusResponse } from '../../../libs/services/DocumentImportService';
import { DdqUploadDialog } from '../ddq-dialog/DdqUploadDialog';

const EmptyGuid = '00000000-0000-0000-0000-000000000000';

const useClasses = makeStyles({
    functional: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.margin('0', '0', tokens.spacingVerticalS, '0'),
    },
    uploadButton: {
        ...shorthands.margin('0', tokens.spacingHorizontalS, '0', '0'),
    },
    viewQuestionsButton: {
        ...shorthands.margin('0', tokens.spacingHorizontalS, '0', '0'),
    },
    vectorDatabase: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
        marginLeft: 'auto',
        ...shorthands.gap(tokens.spacingHorizontalSNudge),
    },
    table: {
        backgroundColor: tokens.colorNeutralBackground1,
    },
    tableHeader: {
        fontWeight: tokens.fontSizeBase600,
    },
    extractingQuestionsLoading: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalSNudge),
    },
    extractingQuestionsCompleted: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalSNudge),
    },
});

interface TableItem {
    id: string;
    chatId: string;
    name: {
        label: string;
        icon: JSX.Element;
        url?: string;
    };
    createdOn: {
        label: string;
        timestamp: number;
    };
    tokens: number;
    documentUploadState: DocumentUploadState;
    status?: string;
    viewQuestionsUri?: string;
}

interface DDQTabProps {
    hiddenChat: boolean;
}

export interface IUploadInfo {
    ddqPrompt?: string;
    selectedFund?: string;
}

export const DDQTab: React.FC<DDQTabProps> = (props) => {
    const classes = useClasses();
    const chat = useChat();
    const dispatch = useDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { importingDocuments } = conversations[selectedId];
    const [documentStatusList, setDocumentStatusList] = React.useState<IQuestionsStatusResponse[]>([]);
    const [resources, setResources] = React.useState<ChatMemorySource[]>([]);
    const [openUploadDialog, setOpenUploadDialog] = React.useState(false);
    const [funds, setFunds] = React.useState<IFundInfo[]>([]);
    const [uploadInfo, setUploadInfo] = React.useState<IUploadInfo | undefined>(undefined);
    const documentFileRef = useRef<HTMLInputElement | null>(null);

    const getFundList = async () => {
        const fundsList = await chat.getFundsAsync();

        if (fundsList?.length) {
            setFunds(fundsList);
        }
    };

    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getFundList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (uploadInfo) {
            documentFileRef.current?.click();
        }
    }, [uploadInfo]);

    React.useEffect(() => {
        const checkQuestionExtractionStatusAsync = async (chatId: string) => {
            const questionsStatusResponse = await chat.checkQuestionExtractionStatusAsync(chatId);
            setDocumentStatusList(questionsStatusResponse ?? []);
        };

        const importingResources = importingDocuments
            ? importingDocuments.map((document, index) => {
                  return {
                      id: `in-progress-${index}`,
                      chatId: selectedId,
                      sourceType: 'N/A',
                      name: document,
                      sharedBy: 'N/A',
                      createdOn: 0,
                      tokens: 0,
                      documentUploadState: DocumentUploadState.InProgress,
                  } as ChatMemorySource;
              })
            : [];

        setResources(importingResources);

        void chat.getChatMemorySources(selectedId).then((sources) => {
            const allSources = [...sources, ...importingResources];

            setResources(allSources);
            setDocumentStatusList([]);

            if (allSources.length) {
                void checkQuestionExtractionStatusAsync(selectedId);
            }
        });

        // We don't want to have chat as one of the dependencies as it will cause infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importingDocuments, selectedId]);

    const onImport = async () => {
        const files = documentFileRef.current?.files;

        if (files && files.length > 0) {
            // Deep copy the FileList into an array so that the function
            // maintains a list of files to import before the import is complete.
            const filesArray = Array.from(files);

            dispatch(
                setImportingDocumentsToConversation({
                    importingDocuments: filesArray.map((file) => file.name),
                    chatId: selectedId,
                }),
            );

            const importDDQDocumentResponse = await chat.importDDQDocument(selectedId, filesArray, uploadInfo);

            if (importDDQDocumentResponse.statusQueryGetUri) {
                //eslint-disable-next-line @typescript-eslint/no-misused-promises
                const interval = setInterval(async () => {
                    const questionsStatusResponse = await chat.checkQuestionExtractionStatusAsync(
                        selectedId,
                        importDDQDocumentResponse.statusQueryGetUri,
                        importDDQDocumentResponse.documentName,
                        importDDQDocumentResponse.sessionId,
                    );

                    setDocumentStatusList(questionsStatusResponse ?? []);

                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                    if (questionsStatusResponse?.filter((d) => d.status?.runtimeStatus === 'Completed').length) {
                        clearInterval(interval);
                    }
                }, 5000);
            }

            dispatch(
                setImportingDocumentsToConversation({
                    importingDocuments: [],
                    chatId: selectedId,
                }),
            );
        }

        // Reset the file input so that the onChange event will
        // be triggered even if the same file is selected again.
        if (documentFileRef.current?.value) {
            documentFileRef.current.value = '';
        }
    };

    const handleImport = () => {
        setOpenUploadDialog(false);
        onImport().catch((e: any) => {
            console.error(`Unable to import document. Details: ${e instanceof Error ? e.message : String(e)}`);
        });
    };

    const onOpenUploadDialog = () => {
        setOpenUploadDialog(true);
    };

    const { columns, rows } = useTable(resources, documentStatusList, classes);

    return (
        <TabView
            title="Due Diligence Questionnaires"
            description="Once you upload a questionnaire, it will be picked up by GPT to extract questions and get answers. 
                The process may take a significant amount of time depending on the size of the document. 
                You can continue with other tasks while the process is running. You will be notified once the process is complete."
        >
            <div className={classes.functional}>
                {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                <input
                    type="file"
                    ref={documentFileRef}
                    style={{ display: 'none' }}
                    accept=".txt,.pdf,.md,.jpg,.jpeg,.png,.tif,.tiff,.docx,.xlsx"
                    multiple={false}
                    onChange={handleImport}
                />
                <Tooltip content="Embed file into chat session" relationship="label">
                    <Button
                        className={classes.uploadButton}
                        icon={<DocumentArrowUp20Regular />}
                        disabled={(importingDocuments && importingDocuments.length > 0) ?? props.hiddenChat}
                        onClick={onOpenUploadDialog}
                    >
                        Upload
                    </Button>
                </Tooltip>
                {importingDocuments && importingDocuments.length > 0 && <Spinner size="tiny" />}
            </div>
            <DdqUploadDialog
                title="Provide additional information"
                open={openUploadDialog}
                funds={funds}
                onUpload={(ddqPrompt, selectedFund) => {
                    setUploadInfo({ ddqPrompt, selectedFund });
                }}
                onClose={() => {
                    setOpenUploadDialog(false);
                }}
            />
            <Table aria-label="External resource table" className={classes.table}>
                <TableHeader>
                    <TableRow>{columns.map((column) => column.renderHeaderCell())}</TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((item) => (
                        <TableRow key={item.id}>{columns.map((column) => column.renderCell(item))}</TableRow>
                    ))}
                </TableBody>
            </Table>
        </TabView>
    );
};

function useTable(
    resources: ChatMemorySource[],
    documentStatusList: IQuestionsStatusResponse[] = [],
    classes: Record<string, string>,
) {
    const headerSortProps = (columnId: TableColumnId): TableHeaderCellProps => ({
        onClick: (e: React.MouseEvent) => {
            toggleColumnSort(e, columnId);
        },
        sortDirection: getSortDirection(columnId),
    });

    const columns: Array<TableColumnDefinition<TableItem>> = [
        createTableColumn<TableItem>({
            columnId: 'name',
            renderHeaderCell: () => (
                <TableHeaderCell key="name" {...headerSortProps('name')}>
                    Name
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={item.id}>
                    <TableCellLayout media={item.name.icon}>{item.name.label}</TableCellLayout>
                </TableCell>
            ),
            compare: (a, b) => {
                const comparison = a.name.label.localeCompare(b.name.label);
                return getSortDirection('name') === 'ascending' ? comparison : comparison * -1;
            },
        }),
        createTableColumn<TableItem>({
            columnId: 'createdOn',
            renderHeaderCell: () => (
                <TableHeaderCell key="createdOn" {...headerSortProps('createdOn')}>
                    Created on
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={item.createdOn.timestamp} title={new Date(item.createdOn.timestamp).toLocaleString()}>
                    {item.documentUploadState === DocumentUploadState.InProgress ? 'N/A' : item.createdOn.label}
                </TableCell>
            ),
            compare: (a, b) => {
                const comparison = a.createdOn.timestamp > b.createdOn.timestamp ? 1 : -1;
                return getSortDirection('createdOn') === 'ascending' ? comparison : comparison * -1;
            },
        }),
        createTableColumn<TableItem>({
            columnId: 'progress',
            renderHeaderCell: () => (
                <TableHeaderCell key="progress" {...headerSortProps('progress')}>
                    Progress
                </TableHeaderCell>
            ),
            renderCell: (item) => (
                <TableCell key={`${item.id}-progress`}>
                    {item.documentUploadState === DocumentUploadState.InProgress ? (
                        <ProgressBar
                            max={1}
                            value={undefined} // Hack: tokens stores the progress bar percentage.
                            shape="rounded"
                            thickness="large"
                            color={'brand'}
                        />
                    ) : (
                        <div>
                            {item.status === 'Loading' ? (
                                <div className={classes.extractingQuestionsLoading}>
                                    <Spinner size="tiny" />
                                    Checking status...
                                </div>
                            ) : item.status === 'Running' ? (
                                <div className={classes.extractingQuestionsLoading}>
                                    <Spinner size="tiny" />
                                    Extracting questions...
                                </div>
                            ) : item.status === 'Completed' && item.viewQuestionsUri ? (
                                <div className={classes.extractingQuestionsCompleted}>
                                    <Button
                                        appearance="primary"
                                        className={classes.viewQuestionsButton}
                                        icon={<CloudLink20Regular />}
                                        onClick={() => {
                                            window.open(item.viewQuestionsUri, '_blank', 'noopener,noreferrer');
                                        }}
                                    >
                                        View questions
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    )}
                </TableCell>
            ),
            compare: (a, b) => {
                const aAccess = getAccessString(a.chatId);
                const bAccess = getAccessString(b.chatId);
                const comparison = aAccess.localeCompare(bAccess);
                return getSortDirection('progress') === 'ascending' ? comparison : comparison * -1;
            },
        }),
    ];

    const items = resources.map((item) => {
        const document = documentStatusList.find((d) => d.documentName === item.name);

        return {
            id: item.id,
            chatId: item.chatId,
            name: {
                label: item.name,
                icon: getFileIconByFileExtension(item.name),
                url: item.hyperlink,
            },
            createdOn: {
                label: timestampToDateString(item.createdOn),
                timestamp: item.createdOn,
            },
            tokens: item.tokens,
            documentUploadState: item.documentUploadState,
            status: document?.status.runtimeStatus ?? 'Loading',
            viewQuestionsUri: document?.viewQuestionsUri ?? '',
        };
    });

    const {
        sort: { getSortDirection, toggleColumnSort, sortColumn },
    } = useTableFeatures(
        {
            columns,
            items,
        },
        [
            useTableSort({
                defaultSortState: { sortColumn: 'createdOn', sortDirection: 'descending' },
            }),
        ],
    );

    if (sortColumn) {
        items.sort((a, b) => {
            const compare = columns.find((column) => column.columnId === sortColumn)?.compare;
            return compare?.(a, b) ?? 0;
        });
    }

    return { columns, rows: items };
}

function getAccessString(chatId: string) {
    return chatId === EmptyGuid ? 'Global' : 'This chat';
}

export function getFileIconByFileExtension(fileName: string, props: FluentIconsProps = {}) {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.') + 1);
    if (extension === 'pdf') {
        return <DocumentPdfRegular {...props} />;
    }
    return <DocumentTextRegular {...props} />;
}
